import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Download, FileSpreadsheet, Loader2, Globe } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ExportDataTab() {
  const [regions, setRegions] = useState([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [exportingSummary, setExportingSummary] = useState(false);
  const [exportingFull, setExportingFull] = useState(false);
  // Use adminToken for legacy admin login or authToken for unified auth
  const token = localStorage.getItem('adminToken') || localStorage.getItem('authToken');

  const fetchRegions = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/admin/export/regions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRegions(data.regions || []);
        setTotalCandidates(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch regions:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);

  const handleExport = async (type) => {
    const setLoading = type === 'summary' ? setExportingSummary : setExportingFull;
    setLoading(true);
    
    try {
      let url = `${API}/api/admin/export/csv/${type}`;
      const params = new URLSearchParams();
      if (selectedRegion !== 'all') params.set('region', selectedRegion);
      if (selectedStatus !== 'all') params.set('status', selectedStatus);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Export failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const filenameMatch = disposition.match(/filename=(.+)/);
      const filename = filenameMatch ? filenameMatch[1] : `export_${type}.csv`;

      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      
      toast.success(`${type === 'summary' ? 'Summary' : 'Full'} CSV exported successfully`);
    } catch (error) {
      toast.error(error.message || `Failed to export ${type} CSV`);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredCount = () => {
    if (selectedRegion === 'all') return totalCandidates;
    const match = regions.find(r => r.region === selectedRegion);
    return match ? match.count : 0;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Export Assessment Data</h2>
        <p className="text-sm text-gray-500 mt-1">
          Download candidate assessment data as CSV files. Filter by region or status before exporting.
        </p>
      </div>

      {/* Filter Controls */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Filter Options</CardTitle>
          <CardDescription>Select region and status to filter the export</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Region</label>
              <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                <SelectTrigger data-testid="export-region-filter">
                  <SelectValue placeholder="All Regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions ({totalCandidates})</SelectItem>
                  {regions.map(r => (
                    <SelectItem key={r.region} value={r.region}>
                      {r.region.charAt(0).toUpperCase() + r.region.slice(1)} ({r.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger data-testid="export-status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="started">Started</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">
                {getFilteredCount()} candidate{getFilteredCount() !== 1 ? 's' : ''} matching filters
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Summary Export */}
        <Card className="border-2 hover:border-[#00AEEF]/30 transition-colors" data-testid="summary-export-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <FileSpreadsheet className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base">Summary Export</CardTitle>
                <CardDescription>Profile info, status, progress — one row per candidate</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="text-sm text-gray-600">
                <p className="font-medium mb-1">Includes:</p>
                <ul className="list-disc list-inside text-xs space-y-0.5 text-gray-500">
                  <li>Name, email, job title, grade, department</li>
                  <li>Region, country, location</li>
                  <li>Manager info, performance rating</li>
                  <li>Assessment status, progress %, dates</li>
                </ul>
              </div>
              <Button
                onClick={() => handleExport('summary')}
                disabled={exportingSummary}
                className="w-full bg-[#003366] hover:bg-[#003366]/90"
                data-testid="export-summary-btn"
              >
                {exportingSummary ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {exportingSummary ? 'Generating...' : 'Download Summary CSV'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Full Export */}
        <Card className="border-2 hover:border-[#00AEEF]/30 transition-colors" data-testid="full-export-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-50 rounded-lg">
                <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-base">Full Export</CardTitle>
                <CardDescription>Profile + every individual question response</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="text-sm text-gray-600">
                <p className="font-medium mb-1">Includes everything in Summary, plus:</p>
                <ul className="list-disc list-inside text-xs space-y-0.5 text-gray-500">
                  <li>All survey responses (Q4, Q5, Q42, etc.)</li>
                  <li>All table entries (certifications, experience)</li>
                  <li>All text field inputs</li>
                  <li>One column per question across all candidates</li>
                </ul>
              </div>
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-200 bg-amber-50">
                Large file — may take a few seconds
              </Badge>
              <Button
                onClick={() => handleExport('full')}
                disabled={exportingFull}
                className="w-full bg-emerald-700 hover:bg-emerald-800"
                data-testid="export-full-btn"
              >
                {exportingFull ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {exportingFull ? 'Generating...' : 'Download Full Responses CSV'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
